import { BackendCN54BisData, CN54BisRow, CN54BisTableData } from './types';
import { transformSingleNonSampledFlow } from './cn56DataTransformer';

/**
 * Maps backend quarter names to table quarter names
 */
const mapQuarterToTableQuarter = (quarter: string): 'Q1' | 'Q2' | 'Q3' | 'Q4' | 'Total' => {
  switch (quarter) {
    case 'ONE': return 'Q1';
    case 'TWO': return 'Q2';
    case 'THREE': return 'Q3';
    case 'FOUR': return 'Q4';
    default: return quarter as 'Q1' | 'Q2' | 'Q3' | 'Q4' | 'Total';
  }
};

/**
 * Transforms backend CN54Bis data to separate table data for each PPO
 * Now creates separate Format Separated and Mixed sections
 */
export function transformBackendDataToSeparateTableData(backendData: BackendCN54BisData[]): CN54BisTableData[] {
  const tableDataArray: CN54BisTableData[] = [];
  
  // Process each PPO's data separately
  backendData.forEach((ppoData, ppoIndex) => {
    const formatSeparatedRows = transformFlowToTableData(ppoData.formatSeparatedSampledFlow, ppoIndex, 'formatSeparated');
    const mixedRows = transformFlowToTableData(ppoData.mixedSampledFlow, ppoIndex, 'mixed');
    const formatSeparatedNonSampledRows = transformSingleNonSampledFlow(ppoData.formatSeparatedNonSampledFlow, ppoIndex);
    const mixedNonSampledRows = ppoData.mixedNonSampledFlow 
      ? transformSingleNonSampledFlow(ppoData.mixedNonSampledFlow, ppoIndex)
      : [];
    
    // Include PPO data if any section has data
    if (formatSeparatedRows.length > 0 || mixedRows.length > 0 || 
        formatSeparatedNonSampledRows.length > 0 || mixedNonSampledRows.length > 0) {
      tableDataArray.push({
        ppo: ppoData.ppo,
        formatSeparatedRows: formatSeparatedRows.length > 0 ? formatSeparatedRows : undefined,
        formatSeparatedNonSampledRows: formatSeparatedNonSampledRows.length > 0 ? formatSeparatedNonSampledRows : undefined,
        mixedRows: mixedRows.length > 0 ? mixedRows : undefined,
        mixedNonSampledRows: mixedNonSampledRows.length > 0 ? mixedNonSampledRows : undefined,
        backendData: ppoData
      });
    }
  });
  
  return tableDataArray;
}

/**
 * Transforms a flow (formatSeparated or mixed) to table format
 */
function transformFlowToTableData(
  flow: any, 
  ppoIndex: number, 
  flowType: 'formatSeparated' | 'mixed'
): CN54BisRow[] {
  const tableRows: CN54BisRow[] = [];
  
  // Check if flow has data
  if (!flow || flow.empty) {
    return [];
  }
  
  // Get all quarters from the data
  let allQuarters: string[];
  
  if (flowType === 'mixed') {
    // For Mixed Case, always show all quarters (Q1, Q2, Q3, Q4) regardless of data availability
    allQuarters = ['ONE', 'TWO', 'THREE', 'FOUR'];
  } else {
    // For Format Separated Case, use quarters from data or fallback to all quarters
    allQuarters = flow.allQuarters;
    if (!allQuarters || allQuarters.length === 0) {
      // Extract quarters from the actual data
      const quartersFromData = new Set<string>();
      flow.sections?.forEach((section: any) => {
        section.rows?.forEach((row: any) => {
          row.subRows?.forEach((subRow: any) => {
            if (subRow.quarter && !subRow.empty) {
              quartersFromData.add(subRow.quarter);
            }
          });
        });
      });
      allQuarters = Array.from(quartersFromData).sort((a, b) => {
        const order = { 'ONE': 1, 'TWO': 2, 'THREE': 3, 'FOUR': 4 };
        return (order[a as keyof typeof order] || 99) - (order[b as keyof typeof order] || 99);
      });
      // Fallback to default quarters if still empty
      if (allQuarters.length === 0) {
        allQuarters = ['ONE', 'TWO', 'THREE', 'FOUR'];
      }
    }
  }
  
  // For formatSeparated flow, show P/S, G, E, Mixed formats
  // For mixed flow, show P/S, G, E, Mixed (X) formats
  if (flowType === 'formatSeparated') {
    // Create rows for P/S format (combines P and S)
    allQuarters.forEach((quarter: string, quarterIndex: number) => {
      const tableQuarter = mapQuarterToTableQuarter(quarter);
      const pData = getQuarterData(flow, 'P', quarter);
      
      const row: CN54BisRow = {
        id: `${ppoIndex}_formatSeparated_P_${quarterIndex}`,
        format: 'P/S',
        quarter: tableQuarter,
        ...pData
      };
      
      tableRows.push(row);
    });
    
    // Add Total row for P/S format
    const pTotalData = getTotalData(flow, 'P');
    tableRows.push({
      id: `${ppoIndex}_formatSeparated_P_total`,
      format: 'P/S',
      quarter: 'Total',
      ...pTotalData
    });
    
    // Create rows for G format
    allQuarters.forEach((quarter: string, quarterIndex: number) => {
      const tableQuarter = mapQuarterToTableQuarter(quarter);
      const gData = getQuarterData(flow, 'G', quarter);
      
      const row: CN54BisRow = {
        id: `${ppoIndex}_formatSeparated_G_${quarterIndex}`,
        format: 'G',
        quarter: tableQuarter,
        ...gData
      };
      
      tableRows.push(row);
    });
    
    // Add Total row for G format
    const gTotalData = getTotalData(flow, 'G');
    tableRows.push({
      id: `${ppoIndex}_formatSeparated_G_total`,
      format: 'G',
      quarter: 'Total',
      ...gTotalData
    });
    
    // Create rows for E format
    allQuarters.forEach((quarter: string, quarterIndex: number) => {
      const tableQuarter = mapQuarterToTableQuarter(quarter);
      const eData = getQuarterData(flow, 'E', quarter);
      
      const row: CN54BisRow = {
        id: `${ppoIndex}_formatSeparated_E_${quarterIndex}`,
        format: 'E',
        quarter: tableQuarter,
        ...eData
      };
      
      tableRows.push(row);
    });
    
    // Add Total row for E format
    const eTotalData = getTotalData(flow, 'E');
    tableRows.push({
      id: `${ppoIndex}_formatSeparated_E_total`,
      format: 'E',
      quarter: 'Total',
      ...eTotalData
    });
    
    // Create rows for Mixed format (X) - always show, even if empty
    allQuarters.forEach((quarter: string, quarterIndex: number) => {
      const tableQuarter = mapQuarterToTableQuarter(quarter);
      const mixedData = getQuarterData(flow, 'X', quarter);
      
      const row: CN54BisRow = {
        id: `${ppoIndex}_formatSeparated_Mixed_${quarterIndex}`,
        format: 'Mixed',
        quarter: tableQuarter,
        ...mixedData
      };
      
      tableRows.push(row);
    });
    
    // Add Total row for Mixed format
    const mixedTotalData = getTotalData(flow, 'X');
    tableRows.push({
      id: `${ppoIndex}_formatSeparated_Mixed_total`,
      format: 'Mixed',
      quarter: 'Total',
      ...mixedTotalData
    });
  } else {
    // For mixed flow, show P/S, G, E, and Mixed (X) formats
    // Create rows for P/S format (P) - always show, even if empty
    allQuarters.forEach((quarter: string, quarterIndex: number) => {
      const tableQuarter = mapQuarterToTableQuarter(quarter);
      const pData = getQuarterData(flow, 'P', quarter);
      
      const row: CN54BisRow = {
        id: `${ppoIndex}_mixed_P_${quarterIndex}`,
        format: 'P/S',
        quarter: tableQuarter,
        ...pData
      };
      
      tableRows.push(row);
    });
    
    // Add Total row for P/S format
    const pTotalData = getTotalData(flow, 'P');
    tableRows.push({
      id: `${ppoIndex}_mixed_P_total`,
      format: 'P/S',
      quarter: 'Total',
      ...pTotalData
    });
    
    // Create rows for G format
    allQuarters.forEach((quarter: string, quarterIndex: number) => {
      const tableQuarter = mapQuarterToTableQuarter(quarter);
      const gData = getQuarterData(flow, 'G', quarter);
      
      const row: CN54BisRow = {
        id: `${ppoIndex}_mixed_G_${quarterIndex}`,
        format: 'G',
        quarter: tableQuarter,
        ...gData
      };
      
      tableRows.push(row);
    });
    
    // Add Total row for G format
    const gTotalData = getTotalData(flow, 'G');
    tableRows.push({
      id: `${ppoIndex}_mixed_G_total`,
      format: 'G',
      quarter: 'Total',
      ...gTotalData
    });
    
    // Create rows for E format
    allQuarters.forEach((quarter: string, quarterIndex: number) => {
      const tableQuarter = mapQuarterToTableQuarter(quarter);
      const eData = getQuarterData(flow, 'E', quarter);
      
      const row: CN54BisRow = {
        id: `${ppoIndex}_mixed_E_${quarterIndex}`,
        format: 'E',
        quarter: tableQuarter,
        ...eData
      };
      
      tableRows.push(row);
    });
    
    // Add Total row for E format
    const eTotalData = getTotalData(flow, 'E');
    tableRows.push({
      id: `${ppoIndex}_mixed_E_total`,
      format: 'E',
      quarter: 'Total',
      ...eTotalData
    });
    
    // Create rows for Mixed format (X)
    allQuarters.forEach((quarter: string, quarterIndex: number) => {
      const tableQuarter = mapQuarterToTableQuarter(quarter);
      const mixedData = getQuarterData(flow, 'X', quarter);
      
      const row: CN54BisRow = {
        id: `${ppoIndex}_mixed_Mixed_${quarterIndex}`,
        format: 'Mixed',
        quarter: tableQuarter,
        ...mixedData
      };
      
      tableRows.push(row);
    });
    
    // Add Total row for Mixed format
    const mixedTotalData = getTotalData(flow, 'X');
    tableRows.push({
      id: `${ppoIndex}_mixed_Mixed_total`,
      format: 'Mixed',
      quarter: 'Total',
      ...mixedTotalData
    });
  }
  
  return tableRows;
}

/**
 * Transforms backend CN54Bis data to table format (legacy function - combines all PPOs)
 */
export function transformBackendDataToTableData(backendData: BackendCN54BisData[]): CN54BisRow[] {
  const tableRows: CN54BisRow[] = [];
  
  // Process each PPO's data
  backendData.forEach((ppoData, ppoIndex) => {
    const { formatSeparatedSampledFlow, mixedSampledFlow } = ppoData;
    
    // Determine which flow to use - prefer formatSeparatedSampledFlow, fallback to mixedSampledFlow
    const flowToUse = formatSeparatedSampledFlow && !formatSeparatedSampledFlow.empty 
      ? formatSeparatedSampledFlow 
      : mixedSampledFlow && !mixedSampledFlow.empty 
      ? mixedSampledFlow 
      : null;
    
    // Check if we have any flow data available
    if (!flowToUse) {
      // Skip this PPO if no data available
      return;
    }
    
    // Get all quarters from the data - if allQuarters is missing, extract from actual data
    let allQuarters = flowToUse.allQuarters;
    if (!allQuarters || allQuarters.length === 0) {
      // Extract quarters from the actual data
      const quartersFromData = new Set<string>();
      flowToUse.sections?.forEach((section: any) => {
        section.rows?.forEach((row: any) => {
          row.subRows?.forEach((subRow: any) => {
            if (subRow.quarter && !subRow.empty) {
              quartersFromData.add(subRow.quarter);
            }
          });
        });
      });
      allQuarters = Array.from(quartersFromData).sort((a, b) => {
        const order = { 'ONE': 1, 'TWO': 2, 'THREE': 3, 'FOUR': 4 };
        return (order[a as keyof typeof order] || 99) - (order[b as keyof typeof order] || 99);
      });
      // Fallback to default quarters if still empty
      if (allQuarters.length === 0) {
        allQuarters = ['ONE', 'TWO', 'THREE', 'FOUR'];
      }
    }
    
    // Create rows for P/S format (combines P and S)
    allQuarters.forEach((quarter, quarterIndex) => {
      const tableQuarter = mapQuarterToTableQuarter(quarter);
      
      // Get data for P format from flowToUse
      const pData = getQuarterData(flowToUse, 'P', quarter);
      
      const row: CN54BisRow = {
        id: `${ppoIndex}_P_${quarterIndex}`,
        format: 'P/S',
        quarter: tableQuarter,
        ...pData
      };
      
      tableRows.push(row);
    });
    
    // Add Total row for P/S format
    const pTotalData = getTotalData(flowToUse, 'P');
    tableRows.push({
      id: `${ppoIndex}_P_total`,
      format: 'P/S',
      quarter: 'Total',
      ...pTotalData
    });
    
    // Create rows for G format
    allQuarters.forEach((quarter, quarterIndex) => {
      const tableQuarter = mapQuarterToTableQuarter(quarter);
      
      // Get data for G format from flowToUse
      const gData = getQuarterData(flowToUse, 'G', quarter);
      
      const row: CN54BisRow = {
        id: `${ppoIndex}_G_${quarterIndex}`,
        format: 'G',
        quarter: tableQuarter,
        ...gData
      };
      
      tableRows.push(row);
    });
    
    // Add Total row for G format
    const gTotalData = getTotalData(flowToUse, 'G');
    tableRows.push({
      id: `${ppoIndex}_G_total`,
      format: 'G',
      quarter: 'Total',
      ...gTotalData
    });
    
    // Create rows for E format
    allQuarters.forEach((quarter, quarterIndex) => {
      const tableQuarter = mapQuarterToTableQuarter(quarter);
      
      // Get data for E format from flowToUse
      const eData = getQuarterData(flowToUse, 'E', quarter);
      
      const row: CN54BisRow = {
        id: `${ppoIndex}_E_${quarterIndex}`,
        format: 'E',
        quarter: tableQuarter,
        ...eData
      };
      
      tableRows.push(row);
    });
    
    // Add Total row for E format
    const eTotalData = getTotalData(flowToUse, 'E');
    tableRows.push({
      id: `${ppoIndex}_E_total`,
      format: 'E',
      quarter: 'Total',
      ...eTotalData
    });
    
    // Create rows for Mixed format (always show Mixed format, even if no data)
    allQuarters.forEach((quarter, quarterIndex) => {
      const tableQuarter = mapQuarterToTableQuarter(quarter);
      
      // Get data for Mixed format from flowToUse (will be empty if no data)
      const mixedData = flowToUse === mixedSampledFlow && mixedSampledFlow && !mixedSampledFlow.empty 
        ? getQuarterData(mixedSampledFlow, 'X', quarter)
        : {
            a_actual_weight: undefined,
            a_gross_percent_from_sampling: undefined,
            a_ipk_from_sampling: undefined,
            a_estimated_number_of_items: undefined,
            b_actual_weight: undefined,
            b_gross_percent_from_sampling: undefined,
            b_ipk_from_sampling: undefined,
            b_estimated_number_of_items: undefined,
            c_actual_weight: undefined,
            c_gross_percent_from_sampling: undefined,
            c_ipk_from_sampling: undefined,
            c_estimated_number_of_items: undefined,
            d_actual_weight: undefined,
            d_gross_percent_from_sampling: undefined,
            d_ipk_from_sampling: undefined,
            d_estimated_number_of_items: undefined,
            total_actual_weight: undefined,
            total_gross_percent: undefined,
            total_ipk: undefined,
            total_estimated_items: undefined
          };
      
      const row: CN54BisRow = {
        id: `${ppoIndex}_Mixed_${quarterIndex}`,
        format: 'Mixed',
        quarter: tableQuarter,
        ...mixedData
      };
      
      tableRows.push(row);
    });
    
    // Add Total row for Mixed format (always show, even if no data)
    const mixedTotalData = flowToUse === mixedSampledFlow && mixedSampledFlow && !mixedSampledFlow.empty 
      ? getTotalData(mixedSampledFlow, 'X')
      : {
          a_actual_weight: undefined,
          a_gross_percent_from_sampling: undefined,
          a_ipk_from_sampling: undefined,
          a_estimated_number_of_items: undefined,
          b_actual_weight: undefined,
          b_gross_percent_from_sampling: undefined,
          b_ipk_from_sampling: undefined,
          b_estimated_number_of_items: undefined,
          c_actual_weight: undefined,
          c_gross_percent_from_sampling: undefined,
          c_ipk_from_sampling: undefined,
          c_estimated_number_of_items: undefined,
          d_actual_weight: undefined,
          d_gross_percent_from_sampling: undefined,
          d_ipk_from_sampling: undefined,
          d_estimated_number_of_items: undefined,
          total_actual_weight: undefined,
          total_gross_percent: undefined,
          total_ipk: undefined,
          total_estimated_items: undefined
        };
        
    tableRows.push({
      id: `${ppoIndex}_Mixed_total`,
      format: 'Mixed',
      quarter: 'Total',
      ...mixedTotalData
    });
  });
  
  return tableRows;
}

/**
 * Gets quarter data for a specific format and quarter - ONLY backend subRows values, NO calculations
 */
function getQuarterData(flow: any, format: string, quarter: string) {
  const data = {
    a_actual_weight: undefined,
    a_gross_percent_from_sampling: undefined,
    a_ipk_from_sampling: undefined,
    a_estimated_number_of_items: undefined,
    b_actual_weight: undefined,
    b_gross_percent_from_sampling: undefined,
    b_ipk_from_sampling: undefined,
    b_estimated_number_of_items: undefined,
    c_actual_weight: undefined,
    c_gross_percent_from_sampling: undefined,
    c_ipk_from_sampling: undefined,
    c_estimated_number_of_items: undefined,
    d_actual_weight: undefined,
    d_gross_percent_from_sampling: undefined,
    d_ipk_from_sampling: undefined,
    d_estimated_number_of_items: undefined,
    total_actual_weight: undefined,
    total_gross_percent: undefined,
    total_ipk: undefined,
    total_estimated_items: undefined
  };
  
  // Check if flow is null or empty
  if (!flow || flow.empty) {
    return data;
  }
  
  // Extract data directly from backend subRows for each mail category
  flow.sections?.forEach((section: any) => {
    const mailCategory = section.mailCategory;
    const formatRow = section.rows?.find((r: any) => r.mailFormat === format);
    
    if (formatRow) {
      const quarterData = formatRow.subRows?.find((sr: any) => sr.quarter === quarter);
      
      if (quarterData && !quarterData.empty) {
        const category = mailCategory.toLowerCase();
        
        // Use formatted values if available, otherwise fall back to raw values
        if (category === 'a') {
          data.a_actual_weight = quarterData.actualWeightFormatted !== undefined && quarterData.actualWeightFormatted !== null && quarterData.actualWeightFormatted !== '' 
            ? quarterData.actualWeightFormatted : quarterData.actualWeight;
          data.a_gross_percent_from_sampling = quarterData.grossPercent !== null && quarterData.grossPercent !== undefined 
            ? (typeof quarterData.grossPercent === 'string' ? quarterData.grossPercent : `${quarterData.grossPercent.toFixed(2)}%`) : quarterData.grossPercent;
          data.a_ipk_from_sampling = quarterData.ipkFormatted !== undefined && quarterData.ipkFormatted !== null && quarterData.ipkFormatted !== '' 
            ? quarterData.ipkFormatted : quarterData.ipk;
          data.a_estimated_number_of_items = quarterData.estimatedNumberOfItemsFormatted !== undefined && quarterData.estimatedNumberOfItemsFormatted !== null && quarterData.estimatedNumberOfItemsFormatted !== '' 
            ? quarterData.estimatedNumberOfItemsFormatted : quarterData.estimatedNumberOfItems;
        } else if (category === 'b') {
          data.b_actual_weight = quarterData.actualWeightFormatted !== undefined && quarterData.actualWeightFormatted !== null && quarterData.actualWeightFormatted !== '' 
            ? quarterData.actualWeightFormatted : quarterData.actualWeight;
          data.b_gross_percent_from_sampling = quarterData.grossPercent !== null && quarterData.grossPercent !== undefined 
            ? (quarterData.grossPercentFormatted || `${quarterData.grossPercent.toFixed(2)}%`) : quarterData.grossPercent;
          data.b_ipk_from_sampling = quarterData.ipkFormatted !== undefined && quarterData.ipkFormatted !== null && quarterData.ipkFormatted !== '' 
            ? quarterData.ipkFormatted : quarterData.ipk;
          data.b_estimated_number_of_items = quarterData.estimatedNumberOfItemsFormatted !== undefined && quarterData.estimatedNumberOfItemsFormatted !== null && quarterData.estimatedNumberOfItemsFormatted !== '' 
            ? quarterData.estimatedNumberOfItemsFormatted : quarterData.estimatedNumberOfItems;
        } else if (category === 'c') {
          data.c_actual_weight = quarterData.actualWeightFormatted !== undefined && quarterData.actualWeightFormatted !== null && quarterData.actualWeightFormatted !== '' 
            ? quarterData.actualWeightFormatted : quarterData.actualWeight;
          data.c_gross_percent_from_sampling = quarterData.grossPercent !== null && quarterData.grossPercent !== undefined 
            ? (quarterData.grossPercentFormatted || `${quarterData.grossPercent.toFixed(2)}%`) : quarterData.grossPercent;
          data.c_ipk_from_sampling = quarterData.ipkFormatted !== undefined && quarterData.ipkFormatted !== null && quarterData.ipkFormatted !== '' 
            ? quarterData.ipkFormatted : quarterData.ipk;
          data.c_estimated_number_of_items = quarterData.estimatedNumberOfItemsFormatted !== undefined && quarterData.estimatedNumberOfItemsFormatted !== null && quarterData.estimatedNumberOfItemsFormatted !== '' 
            ? quarterData.estimatedNumberOfItemsFormatted : quarterData.estimatedNumberOfItems;
        } else if (category === 'd') {
          data.d_actual_weight = quarterData.actualWeightFormatted !== undefined && quarterData.actualWeightFormatted !== null && quarterData.actualWeightFormatted !== '' 
            ? quarterData.actualWeightFormatted : quarterData.actualWeight;
          data.d_gross_percent_from_sampling = quarterData.grossPercent !== null && quarterData.grossPercent !== undefined 
            ? (quarterData.grossPercentFormatted || `${quarterData.grossPercent.toFixed(2)}%`) : quarterData.grossPercent;
          data.d_ipk_from_sampling = quarterData.ipkFormatted !== undefined && quarterData.ipkFormatted !== null && quarterData.ipkFormatted !== '' 
            ? quarterData.ipkFormatted : quarterData.ipk;
          data.d_estimated_number_of_items = quarterData.estimatedNumberOfItemsFormatted !== undefined && quarterData.estimatedNumberOfItemsFormatted !== null && quarterData.estimatedNumberOfItemsFormatted !== '' 
            ? quarterData.estimatedNumberOfItemsFormatted : quarterData.estimatedNumberOfItems;
        }
      }
    }
  });
  
  // Extract Total column data from mailCategoryTotalSection for this quarter
  const totalFormatRow = flow.mailCategoryTotalSection?.rows?.find((r: any) => r.mailFormat === format);
  if (totalFormatRow) {
    const totalQuarterData = totalFormatRow.subRows?.find((sr: any) => sr.quarter === quarter);
    
    if (totalQuarterData && !totalQuarterData.empty) {
      // Use formatted values if available, otherwise fall back to raw values
      data.total_actual_weight = totalQuarterData.actualWeightFormatted !== undefined && totalQuarterData.actualWeightFormatted !== null && totalQuarterData.actualWeightFormatted !== '' 
        ? totalQuarterData.actualWeightFormatted : totalQuarterData.actualWeight;
      data.total_gross_percent = totalQuarterData.grossPercent !== null && totalQuarterData.grossPercent !== undefined 
        ? (totalQuarterData.grossPercentFormatted || `${totalQuarterData.grossPercent.toFixed(2)}%`) : totalQuarterData.grossPercent;
      data.total_ipk = totalQuarterData.ipkFormatted !== undefined && totalQuarterData.ipkFormatted !== null && totalQuarterData.ipkFormatted !== '' 
        ? totalQuarterData.ipkFormatted : totalQuarterData.ipk;
      data.total_estimated_items = totalQuarterData.estimatedNumberOfItemsFormatted !== undefined && totalQuarterData.estimatedNumberOfItemsFormatted !== null && totalQuarterData.estimatedNumberOfItemsFormatted !== '' 
        ? totalQuarterData.estimatedNumberOfItemsFormatted : totalQuarterData.estimatedNumberOfItems;
    }
  }
  
  // Return raw backend data with no calculations or modifications
  return data;
}

/**
 * Gets total data for a specific format - ONLY backend quarterTotalSubRow values, NO calculations
 */
function getTotalData(flow: any, format: string) {
  const data = {
    a_actual_weight: undefined,
    a_gross_percent_from_sampling: undefined,
    a_ipk_from_sampling: undefined,
    a_estimated_number_of_items: undefined,
    b_actual_weight: undefined,
    b_gross_percent_from_sampling: undefined,
    b_ipk_from_sampling: undefined,
    b_estimated_number_of_items: undefined,
    c_actual_weight: undefined,
    c_gross_percent_from_sampling: undefined,
    c_ipk_from_sampling: undefined,
    c_estimated_number_of_items: undefined,
    d_actual_weight: undefined,
    d_gross_percent_from_sampling: undefined,
    d_ipk_from_sampling: undefined,
    d_estimated_number_of_items: undefined,
    total_actual_weight: undefined,
    total_gross_percent: undefined,
    total_ipk: undefined,
    total_estimated_items: undefined
  };
  
  // Check if flow is null or empty
  if (!flow || flow.empty) {
    return data;
  }
  
  // ONLY use backend quarterTotalSubRow values - NO client-side calculations
  // Extract category-specific totals directly from backend quarterTotalSubRow
  flow.sections?.forEach((section: any) => {
    const mailCategory = section.mailCategory?.toLowerCase();
    const categoryFormatRow = section.rows?.find((r: any) => r.mailFormat === format);
    
    if (categoryFormatRow?.quarterTotalSubRow && !categoryFormatRow.quarterTotalSubRow.empty) {
      const categoryTotal = categoryFormatRow.quarterTotalSubRow;
      
      // Use formatted values if available, otherwise fall back to raw values
      if (mailCategory === 'a') {
        data.a_actual_weight = categoryTotal.actualWeightFormatted !== undefined && categoryTotal.actualWeightFormatted !== null && categoryTotal.actualWeightFormatted !== '' 
          ? categoryTotal.actualWeightFormatted : categoryTotal.actualWeight;
        data.a_gross_percent_from_sampling = categoryTotal.grossPercent !== null && categoryTotal.grossPercent !== undefined 
          ? (categoryTotal.grossPercentFormatted || `${categoryTotal.grossPercent.toFixed(2)}%`) : categoryTotal.grossPercent;
        data.a_ipk_from_sampling = categoryTotal.ipkFormatted !== undefined && categoryTotal.ipkFormatted !== null && categoryTotal.ipkFormatted !== '' 
          ? categoryTotal.ipkFormatted : categoryTotal.ipk;
        data.a_estimated_number_of_items = categoryTotal.estimatedNumberOfItemsFormatted !== undefined && categoryTotal.estimatedNumberOfItemsFormatted !== null && categoryTotal.estimatedNumberOfItemsFormatted !== '' 
          ? categoryTotal.estimatedNumberOfItemsFormatted : categoryTotal.estimatedNumberOfItems;
      } else if (mailCategory === 'b') {
        data.b_actual_weight = categoryTotal.actualWeightFormatted !== undefined && categoryTotal.actualWeightFormatted !== null && categoryTotal.actualWeightFormatted !== '' 
          ? categoryTotal.actualWeightFormatted : categoryTotal.actualWeight;
        data.b_gross_percent_from_sampling = categoryTotal.grossPercent !== null && categoryTotal.grossPercent !== undefined 
          ? (categoryTotal.grossPercentFormatted || `${categoryTotal.grossPercent.toFixed(2)}%`) : categoryTotal.grossPercent;
        data.b_ipk_from_sampling = categoryTotal.ipkFormatted !== undefined && categoryTotal.ipkFormatted !== null && categoryTotal.ipkFormatted !== '' 
          ? categoryTotal.ipkFormatted : categoryTotal.ipk;
        data.b_estimated_number_of_items = categoryTotal.estimatedNumberOfItemsFormatted !== undefined && categoryTotal.estimatedNumberOfItemsFormatted !== null && categoryTotal.estimatedNumberOfItemsFormatted !== '' 
          ? categoryTotal.estimatedNumberOfItemsFormatted : categoryTotal.estimatedNumberOfItems;
      } else if (mailCategory === 'c') {
        data.c_actual_weight = categoryTotal.actualWeightFormatted !== undefined && categoryTotal.actualWeightFormatted !== null && categoryTotal.actualWeightFormatted !== '' 
          ? categoryTotal.actualWeightFormatted : categoryTotal.actualWeight;
        data.c_gross_percent_from_sampling = categoryTotal.grossPercent !== null && categoryTotal.grossPercent !== undefined 
          ? (categoryTotal.grossPercentFormatted || `${categoryTotal.grossPercent.toFixed(2)}%`) : categoryTotal.grossPercent;
        data.c_ipk_from_sampling = categoryTotal.ipkFormatted !== undefined && categoryTotal.ipkFormatted !== null && categoryTotal.ipkFormatted !== '' 
          ? categoryTotal.ipkFormatted : categoryTotal.ipk;
        data.c_estimated_number_of_items = categoryTotal.estimatedNumberOfItemsFormatted !== undefined && categoryTotal.estimatedNumberOfItemsFormatted !== null && categoryTotal.estimatedNumberOfItemsFormatted !== '' 
          ? categoryTotal.estimatedNumberOfItemsFormatted : categoryTotal.estimatedNumberOfItems;
      } else if (mailCategory === 'd') {
        data.d_actual_weight = categoryTotal.actualWeightFormatted !== undefined && categoryTotal.actualWeightFormatted !== null && categoryTotal.actualWeightFormatted !== '' 
          ? categoryTotal.actualWeightFormatted : categoryTotal.actualWeight;
        data.d_gross_percent_from_sampling = categoryTotal.grossPercent !== null && categoryTotal.grossPercent !== undefined 
          ? (categoryTotal.grossPercentFormatted || `${categoryTotal.grossPercent.toFixed(2)}%`) : categoryTotal.grossPercent;
        data.d_ipk_from_sampling = categoryTotal.ipkFormatted !== undefined && categoryTotal.ipkFormatted !== null && categoryTotal.ipkFormatted !== '' 
          ? categoryTotal.ipkFormatted : categoryTotal.ipk;
        data.d_estimated_number_of_items = categoryTotal.estimatedNumberOfItemsFormatted !== undefined && categoryTotal.estimatedNumberOfItemsFormatted !== null && categoryTotal.estimatedNumberOfItemsFormatted !== '' 
          ? categoryTotal.estimatedNumberOfItemsFormatted : categoryTotal.estimatedNumberOfItems;
      }
    }
  });
  
  // Extract overall totals directly from backend quarterTotalSubRow
  let formatRow: any = null;
  
  // Priority 1: mailCategoryTotalSection (most accurate totals)
  if (flow.mailCategoryTotalSection?.rows) {
    formatRow = flow.mailCategoryTotalSection.rows.find((r: any) => r.mailFormat === format);
  }
  
  // Priority 2: nonMixedMailCategoryTotalRows (fallback for non-mixed formats)
  if (!formatRow && flow.nonMixedMailCategoryTotalRows && format !== 'X') {
    formatRow = flow.nonMixedMailCategoryTotalRows.find((r: any) => r.mailFormat === format);
  }
  
  // Priority 3: mixedMailCategoryTotalRow (for mixed format only)
  if (!formatRow && format === 'X' && flow.mixedMailCategoryTotalRow) {
    formatRow = flow.mixedMailCategoryTotalRow;
  }
  
  if (formatRow?.quarterTotalSubRow && !formatRow.quarterTotalSubRow.empty) {
    const totalData = formatRow.quarterTotalSubRow;
    
    // Use formatted values if available, otherwise fall back to raw values
    data.total_actual_weight = totalData.actualWeightFormatted !== undefined && totalData.actualWeightFormatted !== null && totalData.actualWeightFormatted !== '' 
      ? totalData.actualWeightFormatted : totalData.actualWeight;
    data.total_estimated_items = totalData.estimatedNumberOfItemsFormatted !== undefined && totalData.estimatedNumberOfItemsFormatted !== null && totalData.estimatedNumberOfItemsFormatted !== '' 
      ? totalData.estimatedNumberOfItemsFormatted : totalData.estimatedNumberOfItems;
    data.total_ipk = totalData.ipkFormatted !== undefined && totalData.ipkFormatted !== null && totalData.ipkFormatted !== '' 
      ? totalData.ipkFormatted : totalData.ipk;
    data.total_gross_percent = totalData.grossPercent !== null && totalData.grossPercent !== undefined 
      ? (totalData.grossPercentFormatted || `${totalData.grossPercent.toFixed(2)}%`) : totalData.grossPercent;
  }
  
  return data;
}


